import { useId, useRef } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MiniFilePickerProps {
  file: File | null;
  onFileChange: (f: File | null) => void;
  disabled: boolean;
}

/**
 * Compact single-file picker used inside ResubmitDialog.
 * Accepts PDF, DOCX, TIFF, JPEG, PNG up to 50 MB.
 */
export function MiniFilePicker({ file, onFileChange, disabled }: MiniFilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  return (
    <div className="space-y-1.5">
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/tiff,image/jpeg,image/png"
        className="sr-only"
        disabled={disabled}
        onChange={(e) => { onFileChange(e.target.files?.[0] ?? null); }}
      />
      {file ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1 truncate text-foreground">{file.name}</span>
          {!disabled && (
            <button
              type="button"
              onClick={() => { onFileChange(null); }}
              className="text-xs text-muted-foreground hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Remove selected file"
            >
              Remove
            </button>
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Browse files…
        </Button>
      )}
    </div>
  );
}
